import React, { Dispatch, ReactElement, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Col, Modal, Row, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { MapRouteLink } from 'leash/components/mapRouteLink/MapRouteLink';
import {
  ColFormStyled,
  MapRouteSpaceStyled,
  TravelTypeSpaceStyled,
} from 'leash/components/packageExpandedRow/PackageExpandedRow.styles';
import HealthDocumentStatus from 'leash/components/healthDocumentStatus/HealthDocumentStatus';
import useEligibilityTravelMethod from 'leash/hooks/useEligibilityTravelMethod/useEligibilityTravelMethod';
import { apiPackage } from 'utilities/http/api/leash/apiPackage';
import { CardTabs } from 'shared/components/cards/CardsTabs';
import { getNameOfLeashContentFieldById } from 'leash/utilities/localStorage/leashContentStorage';
import { log } from 'utilities/log/log';
import { notify } from 'utilities/notifications/notify';
import { onClickOpenDetailsView } from 'leash/utilities/packages/packages.utilities';
import { PackagesAction } from 'leash/reducers/packages/PackagesReducer';
import { reactEnvironmentVariables } from 'utilities/environmentVariables/environmentVariables';
import { SelectTravelMethod } from 'leash/components/fields/selectTravelMethod/SelectTravelMethod';
import { TravelMethodIdType, TravelTypeIdType } from 'leash/types';
import { TravelPlan } from 'types';

const { Text } = Typography;

type Props = {
  travelPlan: TravelPlan;
  dispatch: Dispatch<PackagesAction>;
};

const { confirm } = Modal;

const updateTravelMethod = async (
  orderId: number,
  travelTypeId: TravelTypeIdType,
  travelMethodId: TravelMethodIdType,
  dispatch: Dispatch<PackagesAction>,
) => {
  const error = 'Travel method assignation failed';
  try {
    const response = await apiPackage.updateTravelMethod(
      orderId,
      travelTypeId,
      travelMethodId,
    );
    if (!response.isOk) {
      notify.error(error);
      return;
    }
    dispatch({
      type: 'SET_TRAVEL_METHOD',
      payload: { orderId, travelMethodId },
    });
    notify.success('Travel method assigned successfully');
  } catch (e) {
    log.debug('onAssignTravelMethod', e);
    notify.error(error);
  }
};

export const onAssignTravelMethod = async (
  orderId: number,
  travelTypeId: TravelTypeIdType,
  travelMethodId: TravelMethodIdType,
  dispatch: Dispatch<PackagesAction>,
) => {
  confirm({
    title: `Are you sure you want to assign ${getNameOfLeashContentFieldById(
      'travelMethods',
      travelMethodId,
    )} as travel method?`,
    icon: <ExclamationCircleOutlined />,
    onOk: () => updateTravelMethod(orderId, travelTypeId, travelMethodId, dispatch),
  });
};

export const PackageExpandedRow = ({ travelPlan, dispatch }: Props): ReactElement => {
  const { travelPackage, orderId, address, puppy } = travelPlan;
  const { travelMethodId, travelTypeId } = travelPackage;
  const [travelMethod, setTravelMethod] = useState<TravelMethodIdType>(travelMethodId);
  const kcrmLink = `${reactEnvironmentVariables.KCRM_URL}${travelPlan.customer.kustomerId}`;
  const {
    loadingEligibilityTravelMethods,
    eligibilityTravelMethods,
  } = useEligibilityTravelMethod(travelTypeId, puppy.id, address);
  const history = useHistory();

  return (
    <Row>
      <Col span="24">
        <Row>
          <ColFormStyled>
            <TravelTypeSpaceStyled size="middle" align="center" direction="horizontal">
              <Text strong>Select method</Text>
              <SelectTravelMethod
                defaultValue={travelMethod}
                onChange={setTravelMethod}
                travelMethods={eligibilityTravelMethods}
                loading={loadingEligibilityTravelMethods}
              />
              <Button
                type="primary"
                disabled={!travelMethod}
                ghost
                onClick={() =>
                  onAssignTravelMethod(orderId, travelTypeId, travelMethod, dispatch)
                }
              >
                Apply
              </Button>
              <HealthDocumentStatus documents={puppy.documents ?? []} />
            </TravelTypeSpaceStyled>

            <MapRouteSpaceStyled size="middle" align="center" direction="horizontal">
              <a href={kcrmLink} target="_blank" rel="noreferrer">
                Go to KCRM
              </a>
              <MapRouteLink
                origin={travelPlan.breeder}
                destination={travelPlan.customer}
              />
              <Button
                type="primary"
                onClick={(e) => onClickOpenDetailsView(travelPlan, e, history)}
              >
                Add travel
              </Button>
            </MapRouteSpaceStyled>
          </ColFormStyled>
        </Row>
        <Row>
          <CardTabs
            puppy={{ puppy: travelPlan.puppy, soldDate: travelPlan.purchasedAt }}
            breeder={{
              titlePrefix: 'Breeder',
              person: travelPlan.breeder,
            }}
            customer={{
              titlePrefix: 'Contact',
              person: travelPlan.customer,
              leadId: travelPlan.leadId,
              deliveryAddress: travelPlan.address,
            }}
          />
        </Row>
      </Col>
    </Row>
  );
};
